/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { toOtpErrorMessage, getMsg91RetryChannel } from '@/hooks/use-msg91-otp';
import { redirect, useActionData } from 'react-router';
import type { Route } from './+types/_empty.login';
import { Link } from '@/components/link';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { routes } from '@/route-paths';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import OtpModal from '@/components/login/otp-modal';
import Msg91OtpPopup from '@/components/login/msg91-otp-popup';
import { LoginGuestWishlistBanner } from '@/components/login/login-guest-wishlist-banner';
import { SocialLoginButtons } from '@/components/buttons/social-login-buttons';
import { isAbsoluteURL, getSafeReturnUrl } from '@/lib/utils';
import { getAppOrigin } from '@/lib/origin';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { updateBasketResource } from '@/middlewares/basket.server';
import { buildUrlFromContext } from '@/lib/url.server';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { getTurnstileSiteKey, getTurnstileMode, isTurnstileEnabled } from '@/lib/turnstile/utils';

// services
import {
    getAuth,
    authorizePasswordless,
    getPasswordLessAccessToken,
    updateAuth as updateAuthServer,
} from '@/middlewares/auth.server';
import { loginRegisteredUser } from '@/lib/api/auth/standard-login.server';
import { authorizeIDP } from '@/lib/api/auth/social-login.server';
import { mergeBasket } from '@/lib/api/basket.server';
import {
    appendWishlistMergeFlag,
    captureGuestWishlistSnapshot,
    mergeWishlist,
    type WishlistMergeResult,
} from '@/lib/api/wishlist.server';
import { getPasswordlessErrorMessageKey, extractErrorMessage } from '@/lib/auth/error-handler';
import { getLogger } from '@/lib/logger.server';
import { enforceTurnstile } from '@/lib/turnstile/enforce.server';

type LoginActionResponse = {
    success: boolean;
    error?: string;
    showOTPForm?: boolean;
    email?: string;
};

type LoginLoaderData = {
    error?: string;
    passwordlessSent?: boolean;
    email?: string;
    mode: string;
    isPasswordlessLoginEnabled: boolean;
    isSocialLoginEnabled: boolean;
    returnUrl?: string | null;
    action?: string | null;
    actionParams?: string | null;
    showOTPForm?: boolean;
    otpLength?: number;
    pageUrl: string;
    /** Number of items in the guest wishlist (0 if guest has none, no list, or SCAPI failed). */
    guestWishlistCount: number;
};

export async function loader({ request, context }: Route.LoaderArgs) {
    const logger = getLogger(context);
    const session = getAuth(context);
    const url = new URL(request.url);
    const pageUrl = buildCanonicalUrl(url.origin, url.pathname, url.search);
    const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));

    // If user is already logged in as registered user, redirect to returnUrl or home
    const { accessToken, accessTokenExpiry, userType, customerId } = session;
    if (
        accessToken &&
        typeof accessTokenExpiry === 'number' &&
        accessTokenExpiry >= Date.now() &&
        userType === 'registered' &&
        customerId
    ) {
        logger.debug('Login: already authenticated, redirecting');
        return redirect(buildUrlFromContext(returnUrl || routes.home, context));
    }

    const passwordlessSent = url.searchParams.get('passwordless') === 'sent';
    const showOTPForm = url.searchParams.get('otp') === 'true';
    const email = url.searchParams.get('email');

    const tokenFromUrl = url.searchParams.get('token');
    const legacyOtpCode = url.searchParams.get('otpCode');
    const token = tokenFromUrl ?? legacyOtpCode;
    const pendingAction = url.searchParams.get('action');
    const actionParams = url.searchParams.get('actionParams');
    const error = url.searchParams.get('error');

    const config = getConfig(context);
    const isPasswordlessLoginEnabled = true;
    const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);
    const mode = 'passwordless';

    // Auto-verify OTP token if both email and token are provided in URL
    if (email && token) {
        // Snapshot the guest wishlist BEFORE the SLAS swap; the registered token can't authorize a read against the guest customerId.
        const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

        try {
            const tokenResponse = await getPasswordLessAccessToken(context, token);

            // Update session with auth data. userType, customerId, usid, and the refresh-token
            // expiry cap all derive from the access-token JWT inside updateAuth — no follow-up
            // call is needed.
            updateAuthServer(context, tokenResponse);

            // Merge guest basket with registered user basket
            try {
                await mergeBasket(context);
            } catch (basketError) {
                logger.error('Login: basket merge failed during passwordless login', { error: basketError });
            }

            let wishlistMergeResult: WishlistMergeResult | null = null;
            if (guestWishlistSnapshot) {
                try {
                    wishlistMergeResult = await mergeWishlist(context, guestWishlistSnapshot);
                } catch (wishlistError) {
                    logger.error('Login: wishlist merge failed during passwordless login', { error: wishlistError });
                }
            }

            logger.info('Login: passwordless verification succeeded');
            const target = buildUrlFromContext(returnUrl || routes.home, context);
            if (wishlistMergeResult) {
                const { url: redirectUrl, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                return redirect(redirectUrl, { headers: { 'Set-Cookie': setCookie } });
            }
            return redirect(target);
        } catch (verifyError) {
            // Auto-verification failed - show error with OTP form
            logger.warn('Login: passwordless auto-verification failed');
            const errorMessage = extractErrorMessage(verifyError);
            const errorKey = getPasswordlessErrorMessageKey(errorMessage);
            const { t } = getTranslation(context);

            return {
                error: t(errorKey),
                showOTPForm: true,
                email,
                mode,
                isPasswordlessLoginEnabled,
                isSocialLoginEnabled,
                returnUrl,
                action: pendingAction,
                actionParams,
                otpLength: config.auth.otpLength,
                pageUrl,
                guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
            };
        }
    }

    // Drives the guest-saved-items banner above the form. Helper gates on guest userType
    // and swallows SCAPI errors — a missing snapshot must not block sign-in.
    const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

    return {
        error,
        passwordlessSent,
        showOTPForm,
        email,
        mode,
        isPasswordlessLoginEnabled,
        isSocialLoginEnabled,
        returnUrl,
        action: pendingAction,
        actionParams,
        otpLength: config.auth.otpLength,
        pageUrl,
        guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
    };
}

/**
 * Server action for authentication. Login is handled server-side for security (httpOnly cookies,
 * PKCE code verifier storage) and integration with SLAS.
 *
 * Returns `LoginActionResponse | Response` because the action has two distinct outcomes:
 * - `Response` (redirect) — successful login or social IDP authorization. React Router intercepts
 *   the redirect before it reaches the component, so `useActionData()` never sees it.
 * - `LoginActionResponse` — errors or intermediate states (e.g., OTP form). This data is
 *   serialized and delivered to the component via `useActionData()` for rendering.
 */
export async function action({ request, context }: Route.ActionArgs): Promise<LoginActionResponse | Response> {
    const logger = getLogger(context);
    const config = getConfig(context);
    const { t } = getTranslation(context);
    const genericError = t('errors:genericTryAgain');

    try {
        const formData = await request.formData();

        const email = formData.get('email')?.toString();
        const password = formData.get('password')?.toString();
        const loginMode = formData.get('loginMode')?.toString();
        const provider = formData.get('provider')?.toString();
        const redirectPath = formData.get('redirectPath')?.toString();
        const skipUsid = formData.get('skipUsid') === 'true';
        const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);

        if (loginMode === 'social') {
            // Social login flow
            if (!provider || !isSocialLoginEnabled) {
                return { success: false, error: genericError };
            }
            const socialCallback = config.features.socialLogin.callbackUri;
            const socialLoginRedirectURI = isAbsoluteURL(socialCallback)
                ? socialCallback
                : `${getAppOrigin(context)}${buildUrlFromContext(socialCallback, context)}`;
            const finalRedirectURI = redirectPath
                ? `${socialLoginRedirectURI}?redirectUrl=${redirectPath}`
                : socialLoginRedirectURI;
            const result = await authorizeIDP(context, {
                hint: provider,
                redirectURI: finalRedirectURI,
            });
            if (result.success && result.redirectUrl) {
                logger.info('Login: social redirect initiated', { provider });
                return redirect(result.redirectUrl);
            }
            logger.warn('Login: social authorization failed', { provider });
            return { success: false, error: genericError };
        } else if (loginMode === 'passwordless') {
            // Passwordless login flow
            if (!email) {
                return { success: false, error: genericError };
            }

            const turnstileToken = formData.get('turnstileToken')?.toString();
            const allowed = await enforceTurnstile({
                request,
                config,
                turnstileToken,
                logger,
                actionName: 'login-passwordless',
                email,
            });
            if (!allowed) {
                return { success: false, error: t('errors:api.forbidden') };
            }

            // Build redirectPath from returnUrl, action, and actionParams for passwordless flow
            const url = new URL(request.url);
            const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));
            const pendingAction = url.searchParams.get('action');
            const actionParams = url.searchParams.get('actionParams');

            // Construct redirectPath with all params encoded
            let finalRedirectPath = redirectPath || returnUrl || routes.home;
            if (returnUrl && (pendingAction || actionParams)) {
                const redirectParams = new URLSearchParams();
                if (pendingAction) {
                    redirectParams.set('action', pendingAction);
                }
                if (actionParams) {
                    redirectParams.set('actionParams', actionParams);
                }
                const queryString = redirectParams.toString();
                finalRedirectPath = queryString ? `${returnUrl}?${queryString}` : returnUrl;
            }

            try {
                await authorizePasswordless(context, { userid: email, redirectPath: finalRedirectPath });
                logger.info('Login: passwordless code sent');
                // Passwordless authorization sent - redirect to login page with OTP form
                const params = new URLSearchParams();
                params.set('otp', 'true');
                params.set('email', email);
                if (returnUrl) {
                    params.set('returnUrl', returnUrl);
                }
                if (pendingAction) {
                    params.set('action', pendingAction);
                }
                if (actionParams) {
                    params.set('actionParams', actionParams);
                }
                return { success: true, showOTPForm: true, email };
            } catch (error) {
                const errorMessage = extractErrorMessage(error);
                const errorKey = getPasswordlessErrorMessageKey(errorMessage);

                return { success: false, error: t(errorKey) };
            }
        } else {
            // Standard password login flow (default case - handles 'password' mode or undefined/null)
            if (!email || !password) {
                return { success: false, error: genericError };
            }
            // Snapshot the guest wishlist BEFORE the SLAS swap; the registered token can't authorize a read against the guest customerId.
            const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);
            const result = await loginRegisteredUser(context, { email, password }, { skipUsid });
            if (!result.success) {
                logger.warn('Login: standard login failed');
                return { success: false, error: genericError };
            }

            logger.info('Login: standard login succeeded');
            // Login successful - merge basket on server before redirecting
            try {
                const mergedBasket = await mergeBasket(context);
                if (mergedBasket) {
                    updateBasketResource(context, mergedBasket);
                }
            } catch (error) {
                logger.error('Login: basket merge failed', { error });
            }

            let wishlistMergeResult: WishlistMergeResult | null = null;
            if (guestWishlistSnapshot) {
                try {
                    wishlistMergeResult = await mergeWishlist(context, guestWishlistSnapshot);
                } catch (wishlistError) {
                    logger.error('Login: wishlist merge failed', { error: wishlistError });
                }
            }

            // Helper to prepare redirect with wishlist merge cookie
            const prepareRedirect = (target: string) => {
                if (wishlistMergeResult) {
                    const { url, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                    return redirect(url, { headers: { 'Set-Cookie': setCookie } });
                }
                return redirect(target);
            };

            // Login successful - redirect to returnUrl if provided, otherwise home
            // Try to get returnUrl from formData first (in case it was submitted as hidden input)
            // Otherwise fall back to URL query params
            const returnUrlFromForm = formData.get('returnUrl')?.toString()?.trim();
            const returnUrlFromUrl = new URL(request.url).searchParams.get('returnUrl');
            const returnUrl = getSafeReturnUrl(returnUrlFromForm || returnUrlFromUrl);

            // Get action and actionParams to preserve in redirect URL
            const actionFromForm = formData.get('action')?.toString();
            const actionParamsFromForm = formData.get('actionParams')?.toString();
            const actionFromUrl = new URL(request.url).searchParams.get('action');
            const actionParamsFromUrl = new URL(request.url).searchParams.get('actionParams');
            const pendingAction = actionFromForm || actionFromUrl;
            const actionParams = actionParamsFromForm || actionParamsFromUrl;

            // Redirect to returnUrl (with preserved action params) or home.
            // `returnUrl` is guaranteed relative by `getSafeReturnUrl` above, so we mutate
            // its query string in place rather than constructing a `URL` object — that
            // keeps the redirect target relative end-to-end and removes any temptation
            // for a future refactor to forward an absolute URL past the open-redirect
            // guard.
            if (returnUrl) {
                if (pendingAction || actionParams) {
                    const [pathAndQuery, fragment] = returnUrl.split('#');
                    const [pathname, existingQuery] = pathAndQuery.split('?');
                    const params = new URLSearchParams(existingQuery);
                    if (pendingAction) params.set('action', pendingAction);
                    if (actionParams) params.set('actionParams', actionParams);
                    const queryString = params.toString();
                    const target = pathname + (queryString ? `?${queryString}` : '') + (fragment ? `#${fragment}` : '');
                    return prepareRedirect(buildUrlFromContext(target, context));
                }
                return prepareRedirect(buildUrlFromContext(returnUrl, context));
            }

            return prepareRedirect(buildUrlFromContext(routes.home, context));
        }
    } catch {
        return { success: false, error: genericError };
    }
}

export default function Login({ loaderData }: { loaderData: LoginLoaderData }): ReactElement {
    const { t } = useTranslation('login');
    const actionData = useActionData<typeof action>();
    const navigate = useNavigate();
    const config = useConfig();

    const {
        error: loaderError,
        passwordlessSent,
        email,
        isSocialLoginEnabled,
        returnUrl,
        showOTPForm,
        otpLength,
        pageUrl,
        guestWishlistCount,
    } = loaderData;

    const [resendTurnstileToken, setResendTurnstileToken] = useState<string | null>(null);
    const resendTurnstileResetRef = useRef<(() => void) | null>(null);

    // MSG91 & Cross-site state
    const [msg91Error, setMsg91Error] = useState<string | null>(null);
    const [checkingUser, setCheckingUser] = useState<boolean>(false);
    const [msg91Email, setMsg91Email] = useState<string>('');
    const [showMockOtp, setShowMockOtp] = useState<boolean>(false);
    const [mockOtpCode, setMockOtpCode] = useState<string>('');
    const [verifyingMockOtp, setVerifyingMockOtp] = useState<boolean>(false);
    const [userCheckStatus, setUserCheckStatus] = useState<string | null>(null);

    // Custom MSG91 OTP popup state (replaces MSG91's default hosted popup UI)
    const [showMsg91OtpPopup, setShowMsg91OtpPopup] = useState<boolean>(false);
    const [msg91MaskedIdentifier, setMsg91MaskedIdentifier] = useState<string>('');
    const [msg91PopupError, setMsg91PopupError] = useState<string | null>(null);
    const [msg91ResolvedEmail, setMsg91ResolvedEmail] = useState<string | null>(null);
    const [verifyingMsg91Otp, setVerifyingMsg91Otp] = useState<boolean>(false);
    const msg91WidgetInitializedRef = useRef(false);
    const msg91ReqIdRef = useRef<string | null>(null);
    const msg91ResendCountRef = useRef(0);
    const MSG91_MAX_RESEND = 3;

    const turnstileEnabled = config ? isTurnstileEnabled(config) : false;
    const turnstileMode = config ? getTurnstileMode(config) : 'managed';
    const turnstileSiteKey = useMemo(() => {
        if (!config || !turnstileEnabled) return null;
        if (typeof window !== 'undefined') {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            return getTurnstileSiteKey(config, baseUrl);
        }
        return null;
    }, [config, turnstileEnabled]);

    const handleResendTurnstileSuccess = useCallback((token: string) => {
        setResendTurnstileToken(token);
    }, []);
    const handleResendTurnstileError = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);
    const handleResendTurnstileExpire = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);

    // Load MSG91 script dynamically
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if ((window as any).initSendOTP) return;

        const urls = ['https://verify.msg91.com/otp-provider.js', 'https://verify.phone91.com/otp-provider.js'];

        let i = 0;
        function attempt() {
            const s = document.createElement('script');
            s.src = urls[i];
            s.async = true;
            s.onerror = () => {
                i++;
                if (i < urls.length) {
                    attempt();
                }
            };
            document.head.appendChild(s);
        }
        attempt();
    }, []);

    // Masks a phone/email identifier for display in the OTP popup, e.g.
    // "919876543210" -> "+91 ******3210", "jane.doe@example.com" -> "j***e@example.com"
    const maskMsg91Identifier = (identifier: string): string => {
        if (identifier.includes('@')) {
            const [user, domain] = identifier.split('@');
            if (user.length <= 2) return `${user[0]}***@${domain}`;
            return `${user[0]}${'*'.repeat(Math.max(user.length - 2, 3))}${user[user.length - 1]}@${domain}`;
        }
        const digits = identifier.replace(/\D/g, '');
        if (digits.length <= 4) return digits;
        const countryCode = digits.slice(0, digits.length - 10) || digits.slice(0, 2);
        const last4 = digits.slice(-4);
        const maskedLength = digits.length - countryCode.length - 4;
        return `+${countryCode} ${'*'.repeat(Math.max(maskedLength, 0))}${last4}`;
    };

    // Verify OTP using the custom backend verification route
    const verifyMsg91OtpAndLogin = async (
        emailValue: string,
        accessToken: string,
        onError: (message: string) => void,
        resolvedEmailValue?: string | null
    ) => {
        try {
            setCheckingUser(true);

            const verifyFormData = new FormData();
            verifyFormData.append('email', emailValue);
            if (resolvedEmailValue) {
                verifyFormData.append('resolvedEmail', resolvedEmailValue);
            }
            verifyFormData.append('accessToken', accessToken);

            const response = await fetch('/action/verify-msg91-otp', {
                method: 'POST',
                body: verifyFormData,
            });

            const result = await response.json();
            setCheckingUser(false);

            if (result.success) {
                // Redirect to returnUrl or home
                const target = returnUrl || routes.home;
                if (result.wishlistMerge) {
                    const separator = target.includes('?') ? '&' : '?';
                    window.location.href = `${target}${separator}wishlistMerge=${result.wishlistMerge}`;
                } else {
                    window.location.href = target;
                }
            } else {
                onError(result.error?.message || 'Authentication failed. Please check the code.');
            }
        } catch (error: any) {
            setCheckingUser(false);
            onError(error.message || 'Verification connection failed.');
        }
    };

    // Triggers the MSG91 "send OTP" call and opens our own custom popup UI in place of
    // MSG91's default hosted popup (exposeMethods:true suppresses MSG91's own UI).
    const startMsg91Otp = (identifier: string) => {
        const widgetId = config?.features?.msg91?.widgetId || '3666656a4470343931353433';
        const tokenAuth = config?.features?.msg91?.tokenAuth || '523025TVi0B5Cp4166a22a5caP1';
        const w = window as any;

        const callSendOtp = () => {
            w.sendOtp(
                identifier,
                (data: any) => {
                    msg91ReqIdRef.current = data?.message || data?.reqId || null;
                    msg91ResendCountRef.current = 0;
                    setMsg91MaskedIdentifier(maskMsg91Identifier(identifier));
                    setMsg91PopupError(null);
                    setShowMsg91OtpPopup(true);
                },
                (err: any) => {
                    setMsg91Error('Failed to send OTP: ' + (err?.message || err));
                }
            );
        };

        if (!msg91WidgetInitializedRef.current) {
            if ((w as any).Zone?.assertZonePatched) {
                (w as any).Zone.assertZonePatched = () => {};
            }
            w.initSendOTP({
                widgetId,
                tokenAuth,
                exposeMethods: true,
                success: () => {},
                failure: () => {},
            });
            msg91WidgetInitializedRef.current = true;

            // initSendOTP does async setup before attaching window.sendOtp, so
            // it isn't guaranteed to be ready on this same tick — poll briefly.
            let attempts = 0;
            const waitForSendOtp = () => {
                if (typeof w.sendOtp === 'function') {
                    callSendOtp();
                } else if (attempts < 20) {
                    attempts++;
                    setTimeout(waitForSendOtp, 100);
                } else {
                    setMsg91Error('Failed to initialize OTP widget. Please try again.');
                }
            };
            waitForSendOtp();
        } else {
            callSendOtp();
        }
    };

    // Submits email for presence check and triggers MSG91 widget or Mock OTP
    const handleMsg91Submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMsg91Error(null);
        setCheckingUser(true);
        setUserCheckStatus(null);
        setShowMockOtp(false);

        const form = e.currentTarget;
        const emailInput = form.elements.namedItem('email') as HTMLInputElement;
        const emailValue = emailInput.value.trim();

        // Normalize 10-digit phone number to prepend '91' and strip '+' prefix if present
        let normalizedEmailValue = emailValue;
        const cleanDigits = emailValue.replace(/\D/g, '');
        if (/^\d{10}$/.test(cleanDigits)) {
            normalizedEmailValue = `91${cleanDigits}`;
        } else if (emailValue.startsWith('+')) {
            normalizedEmailValue = emailValue.substring(1);
        }

        setMsg91Email(normalizedEmailValue);

        try {
            const formData = new FormData();
            formData.append('email', normalizedEmailValue);

            const response = await fetch('/action/pre-login', {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();
            setCheckingUser(false);

            if (!result.success) {
                if (result.status === 'not_found' || result.error?.message?.includes('sign up')) {
                    setMsg91Error(result.error?.message || 'Account not found. Redirecting to signup...');
                    setTimeout(() => {
                        void navigate(`${routes.signup}?email=${encodeURIComponent(normalizedEmailValue)}`);
                    }, 3000);
                } else {
                    setMsg91Error(result.error?.message || 'Verification check failed. Please try again.');
                }
                return;
            }

            if (result.email) {
                setMsg91ResolvedEmail(result.email);
            }

            setUserCheckStatus(result.status);

            if (typeof (window as any).initSendOTP === 'function') {
                startMsg91Otp(normalizedEmailValue);
            } else {
                setMsg91Error('MSG91 widget failed to load. Falling back to mock mode for testing.');
                setShowMockOtp(true);
            }
        } catch (error: any) {
            setCheckingUser(false);
            setMsg91Error(error.message || 'An error occurred during verification.');
        }
    };

    // Called from the custom OTP popup when the user submits a code
    const handleMsg91PopupVerify = (code: string) => {
        setVerifyingMsg91Otp(true);
        setMsg91PopupError(null);
        const w = window as any;
        w.verifyOtp(
            code,
            async (data: any) => {
                const accessToken = data?.message || data?.data || data;
                await verifyMsg91OtpAndLogin(
                    msg91Email,
                    accessToken,
                    (message) => {
                        setVerifyingMsg91Otp(false);
                        setMsg91PopupError(message);
                    },
                    msg91ResolvedEmail
                );
            },
            (err: any) => {
                setVerifyingMsg91Otp(false);
                setMsg91PopupError(toOtpErrorMessage(err?.message));
            },
            msg91ReqIdRef.current
        );
    };

    // Called from the custom OTP popup's "Resend OTP" link
    const handleMsg91PopupResend = () => {
        if (msg91ResendCountRef.current >= MSG91_MAX_RESEND) {
            setMsg91PopupError('You have reached the maximum number of resend attempts.');
            return;
        }

        const w = window as any;
        try {
        w.retryOtp(
            getMsg91RetryChannel(msg91Email),
            (data: any) => {
                msg91ResendCountRef.current += 1;
                if (data?.message || data?.reqId) {
                    msg91ReqIdRef.current = data.message || data.reqId;
                }
            },
            (err: any) => {
                setMsg91PopupError(err?.message || 'Failed to resend code. Please try again.');
            },
            msg91ReqIdRef.current
        );
     } catch (err: any) {
            // The widget throws (rather than calling the error callback) for invalid input — show it instead of failing silently
            setMsg91PopupError(err?.message || 'Failed to resend code. Please try again.');
        }
    };

    // "Edit" link in the popup — go back to the identifier form without losing the entered value
    const handleMsg91PopupEdit = () => {
        setShowMsg91OtpPopup(false);
        setMsg91PopupError(null);
    };

    // Submits the simulated OTP code
    const handleMockOtpVerify = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!mockOtpCode) return;
        setVerifyingMockOtp(true);
        const mockAccessToken = `mock-otp-token-${msg91Email}`;
        await verifyMsg91OtpAndLogin(msg91Email, mockAccessToken, setMsg91Error, msg91ResolvedEmail);
        setVerifyingMockOtp(false);
    };

    // Check if we should show OTP form from actionData (after email submission) or loaderData (from URL)
    const shouldShowOTPForm = actionData?.showOTPForm || showOTPForm;
    const showOTPModal = Boolean(shouldShowOTPForm);
    const otpEmail = actionData?.email || email;

    // Show passwordless success state
    if (passwordlessSent && email) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background py-12 section-container">
                <div className="max-w-md w-full space-y-8">
                    <Card className="p-8 rounded-none shadow-none">
                        <div className="text-center space-y-4">
                            <h2 className="text-2xl font-semibold">{t('checkEmailTitle')}</h2>
                            <p className="text-sm text-muted-foreground">{t('checkEmailDescription', { email })}</p>
                            <Link
                                to={routes.login}
                                className="inline-flex items-center text-sm text-primary hover:text-primary/80">
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Decide which form to render based on mode
    const renderForm = () => {
        return (
            <div className="space-y-6">
                {msg91Error && (
                    <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded text-sm">
                        {msg91Error}
                    </div>
                )}

                {userCheckStatus && (
                    <div className="bg-green-500/10 border border-green-500/20 text-green-600 px-4 py-2 rounded text-xs">
                        {userCheckStatus === 'copied'
                            ? 'Account successfully migrated from external site. Initializing OTP verification...'
                            : 'Account verified on our site. Initializing OTP verification...'}
                    </div>
                )}

                {showMockOtp ? (
                    <form onSubmit={handleMockOtpVerify} className="space-y-4">
                        <div className="space-y-1">
                            <label htmlFor="mockOtp" className="block text-sm font-medium text-foreground">
                                Verify Mock OTP for {msg91Email}
                            </label>
                            <p className="text-xs text-muted-foreground">
                                Enter any 6-digit code to simulate MSG91 OTP verification.
                            </p>
                            <Input
                                id="mockOtp"
                                name="mockOtp"
                                type="text"
                                maxLength={6}
                                required
                                value={mockOtpCode}
                                onChange={(e) => setMockOtpCode(e.target.value)}
                                placeholder="123456"
                                className="mt-1"
                            />
                        </div>
                        <Button type="submit" disabled={verifyingMockOtp} className="w-full">
                            {verifyingMockOtp ? 'Verifying...' : 'Verify OTP & Login'}
                        </Button>
                    </form>
                ) : (
                    <form onSubmit={handleMsg91Submit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground">
                                Email or Phone Number
                            </label>
                            <Input
                                id="email"
                                name="email"
                                type="text"
                                autoComplete="username"
                                required
                                className="mt-1"
                                placeholder="Enter email or mobile number"
                                disabled={checkingUser}
                            />
                        </div>

                        <Button type="submit" disabled={checkingUser} className="w-full">
                            {checkingUser ? 'Checking account...' : 'Send OTP'}
                        </Button>
                    </form>
                )}
            </div>
        );
    };

    return (
        <>
            <SeoMeta
                title={t('meta.title', { defaultValue: 'Sign In' })}
                description={t('meta.description', {
                    defaultValue: 'Sign in to your account to view orders, manage your wishlist, and more.',
                })}
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div className="min-h-screen flex items-center justify-center bg-background py-12 section-container">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <h2 className="mt-6 text-center text-3xl font-bold text-foreground">{t('title')}</h2>
                        <p className="mt-2 text-center text-sm text-muted-foreground">{t('subtitle')}</p>
                    </div>

                    <LoginGuestWishlistBanner count={guestWishlistCount} />

                    <Card className="p-8 rounded-none shadow-none">
                        {renderForm()}
                        {isSocialLoginEnabled ? <SocialLoginButtons /> : null}
                    </Card>
                </div>
            </div>

            {/* Turnstile widget for OTP resend — hidden, generates tokens for the resend fetch */}
            {showOTPModal && turnstileEnabled && turnstileSiteKey && (
                <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onSuccess={handleResendTurnstileSuccess}
                    onError={handleResendTurnstileError}
                    onExpire={handleResendTurnstileExpire}
                    enabled={turnstileEnabled}
                    mode={turnstileMode}
                    resetRef={resendTurnstileResetRef}
                />
            )}

            {/* Custom MSG91 OTP popup - replaces MSG91's default hosted popup UI */}
            <Msg91OtpPopup
                isOpen={showMsg91OtpPopup}
                maskedIdentifier={msg91MaskedIdentifier}
                otpLength={4}
                isVerifying={verifyingMsg91Otp || checkingUser}
                error={msg91PopupError}
                onClose={() => setShowMsg91OtpPopup(false)}
                onEdit={handleMsg91PopupEdit}
                onVerify={handleMsg91PopupVerify}
                onResend={handleMsg91PopupResend}
            />

            {/* OTP Modal - appears over the login page */}
            <OtpModal
                isOpen={showOTPModal}
                otpLength={otpLength}
                initialError={loaderError}
                onClose={() => {
                    // Navigate back to login without OTP modal
                    const url = new URL(window.location.href);
                    url.searchParams.delete('otp');
                    url.searchParams.delete('error');
                    url.searchParams.delete('token');
                    url.searchParams.delete('email');
                    window.history.replaceState({}, '', url.toString());
                    window.location.reload();
                }}
                email={otpEmail || ''}
                onSuccess={(_tokenResponse, meta) => {
                    // Redirect to return URL or home after successful login.
                    // Forward the wishlist merge flag so the destination route can render a toast.
                    const target = returnUrl || routes.home;
                    if (meta?.wishlistMerge) {
                        const separator = target.includes('?') ? '&' : '?';
                        void navigate(`${target}${separator}wishlistMerge=${meta.wishlistMerge}`);
                    } else {
                        void navigate(target);
                    }
                }}
                onResendCode={async () => {
                    const formData = new FormData();
                    formData.append('email', otpEmail || '');
                    formData.append('loginMode', 'passwordless');
                    if (returnUrl) {
                        formData.append('redirectPath', returnUrl);
                    }
                    if (resendTurnstileToken) {
                        formData.append('turnstileToken', resendTurnstileToken);
                    }
                    await fetch(window.location.pathname, {
                        method: 'POST',
                        body: formData,
                    });
                    if (turnstileEnabled) {
                        setResendTurnstileToken(null);
                        resendTurnstileResetRef.current?.();
                    }
                }}
            />
        </>
    );
}
